import { useState, useContext, useMemo, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Card,
  CardContent,
  Divider,
  alpha,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { UpgradeModal } from '../../components/UpgradeModal';
import { useTierAccess } from '../../hooks/useTierAccess';
import { useCreateOrder } from '../../hooks/useOrderMutations';
import { getUser } from '../../utils/auth';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme as themeConfig } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { ResellerSelector } from './components/ResellerSelector';
import { B2BPricingCalculator } from './components/B2BPricingCalculator';
import type { Product, Location, ProductImage, PaymentMethod } from '../../types';
import { OrderType as OrderTypeEnum, FulfillmentMethod as FulfillmentMethodEnum } from '../../types';
import type { ResellerProfile, PaymentTerms } from '../../types/b2b';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Helper to construct full image URL from relative path
const getImageUrl = (image: ProductImage): string => {
  const path = image.thumbnail || image.medium || image.large;
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${API_URL}/${cleanPath}`;
};

interface CartItem {
  productId: string;
  productName: string;
  quantity: number;
  retailPrice: number;
  product: Product;
}

interface B2BPricingResponse {
  items: {
    productId: string;
    productName: string;
    quantity: number;
    retailPrice: number;
    b2bBasePrice: number;
    tierName: string | null;
    tierDiscount: number;
    tierDiscountAmount: number;
    quantityDiscount: number;
    quantityDiscountAmount: number;
    finalUnitPrice: number;
    subtotal: number;
  }[];
  summary: {
    subtotal: number;
    totalTierDiscount: number;
    totalQuantityDiscount: number;
    totalSavings: number;
    savingsPercentage: number;
    grandTotal: number;
  };
}

interface POSColors {
  bg: string;
  surface: string;
  surfaceLight: string;
  surfaceElevated?: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentLight: string;
  success?: string;
  warning?: string;
  error?: string;
}

// Payment terms options
const paymentTermsOptions: { value: PaymentTerms; label: string }[] = [
  { value: 'due_on_receipt', label: 'Due on Receipt' },
  { value: 'net_7', label: 'Net 7 Days' },
  { value: 'net_15', label: 'Net 15 Days' },
  { value: 'net_30', label: 'Net 30 Days' },
  { value: 'net_60', label: 'Net 60 Days' },
  { value: 'net_90', label: 'Net 90 Days' },
];

// Payment methods
const paymentMethods = [
  { value: 'cash', label: 'Cash', icon: LocalAtmIcon },
  { value: 'card', label: 'Card', icon: CreditCardIcon },
  { value: 'mobile', label: 'Mobile', icon: PhoneAndroidIcon },
];

export const CreateB2BSale = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { tier } = useTierAccess();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();
  const user = getUser();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [selectedReseller, setSelectedReseller] = useState<ResellerProfile | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(user?.locationId || null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerms>('net_30');
  const [notes, setNotes] = useState('');
  const [, setPricingData] = useState<B2BPricingResponse | null>(null);

  // Rate limit modal state
  const [rateLimitModalOpen, setRateLimitModalOpen] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<{ limit: number; resetAt: string } | null>(null);

  const isDark = mode === 'dark';
  const colors: POSColors = isDark ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceLight: palette.surfaceElevated,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
    accent: palette.primary,
    accentLight: alpha(palette.primary, 0.7),
    success: '#22C55E',
    warning: '#F59E0B',
    error: '#EF4444',
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceLight: palette.light.surfaceElevated,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
    accent: palette.primary,
    accentLight: alpha(palette.primary, 0.7),
    success: '#22C55E',
    warning: '#F59E0B',
    error: '#EF4444',
  };

  // Fetch locations (stores)
  const { data: locationsData } = useQuery({
    queryKey: ['b2b-locations'],
    queryFn: async () => {
      const response = await axiosInstance.get('/locations');
      const locations = Array.isArray(response.data) ? response.data : response.data.data || [];
      return locations.filter((loc: Location) => loc.type === 'store' && loc.isActive);
    },
  });

  const locations = useMemo(() => locationsData || [], [locationsData]);

  // Set first location as default if none selected
  useEffect(() => {
    if (locations.length > 0 && !selectedLocationId) {
      setSelectedLocationId(locations[0].id);
    }
  }, [locations, selectedLocationId]);

  // Fetch products for search
  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['b2b-products', searchTerm],
    queryFn: async () => {
      if (searchTerm && searchTerm.trim().length > 0) {
        const response = await axiosInstance.get('/products/search', {
          params: { q: searchTerm.trim() },
        });
        const data = Array.isArray(response.data) ? response.data : response.data.data || [];
        return data.filter((p: Product) => !p.productType || p.productType === 'sellable');
      } else {
        const response = await axiosInstance.get('/products', {
          params: { limit: 50, productType: 'sellable' },
        });
        return Array.isArray(response.data) ? response.data : response.data.data || [];
      }
    },
  });

  const products = useMemo(() => productsData || [], [productsData]);

  // Format price in DZD
  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Cart operations
  const addToCart = (product: Product) => {
    const existingItem = cart.find((item) => item.productId === product.id);
    if (existingItem) {
      setCart(
        cart.map((item) =>
          item.productId === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setCart([
        ...cart,
        {
          productId: product.id,
          productName: product.name,
          quantity: 1,
          retailPrice: product.price,
          product,
        },
      ]);
    }
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(
        cart.map((item) =>
          item.productId === productId ? { ...item, quantity } : item
        )
      );
    }
  };

  const removeFromCart = (productId: string) => {
    setCart(cart.filter((item) => item.productId !== productId));
  };

  const clearCart = () => {
    setCart([]);
  };

  // Create B2B order mutation using unified order system
  const createOrderMutation = useCreateOrder();

  // Handle form submission - create B2B order using unified order system
  const handleSubmit = () => {
    if (!selectedReseller) {
      showMessage('Please select a reseller', 'warning');
      return;
    }

    if (cart.length === 0) {
      showMessage('Please add at least one product', 'warning');
      return;
    }

    if (!selectedLocationId) {
      showMessage('Please select a location', 'warning');
      return;
    }

    // Create order using unified order system with B2B_DIRECT type
    createOrderMutation.mutate(
      {
        orderType: OrderTypeEnum.B2B_DIRECT,
        locationId: selectedLocationId,
        fulfillmentMethod: FulfillmentMethodEnum.PICKUP,
        items: cart.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
        })),
        resellerId: selectedReseller.id,
        pricingTierId: selectedReseller.defaultTierId || undefined,
        paymentMethod: paymentMethod as PaymentMethod,
        staffId: user?.id,
        notes: notes || undefined,
        b2bMetadata: {
          paymentTerms,
        },
      },
      {
        onSuccess: () => {
          showMessage('B2B order created successfully', 'success');
          queryClient.invalidateQueries({ queryKey: ['orders'] });
          queryClient.invalidateQueries({ queryKey: ['b2b-sales'] });
          // Navigate to orders page filtered by B2B
          navigate('/app/orders?orderType=b2b_direct');
        },
        onError: (error: any) => {
          if (error.response?.status === 429 && error.response?.data?.upgradeRequired) {
            setRateLimitInfo({
              limit: error.response.data.limit,
              resetAt: error.response.data.resetAt,
            });
            setRateLimitModalOpen(true);
          } else {
            showMessage(error.response?.data?.message || 'Failed to create B2B order', 'error');
          }
        },
      }
    );
  };

  // Handle pricing calculation callback
  const handlePricingCalculated = (pricing: B2BPricingResponse | null) => {
    setPricingData(pricing);
  };

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Two-column layout */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 400px' },
          gap: 3,
          minHeight: 'calc(100vh - 280px)',
        }}
      >
        {/* LEFT COLUMN - Product Selection */}
        <Box sx={{ ...animations.slideUp(0.1) }}>
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: themeConfig.radius.sm,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              {/* Reseller Selection */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterSpacing.widest,
                    fontFamily: typography.fontFamily.mono,
                    mb: 1.5,
                  }}
                >
                  Reseller
                </Typography>
                <ResellerSelector
                  selectedReseller={selectedReseller}
                  onResellerSelect={setSelectedReseller}
                  colors={colors}
                />
              </Box>

              <Divider sx={{ my: 3, borderColor: colors.border }} />

              {/* Location Selection */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterSpacing.widest,
                    fontFamily: typography.fontFamily.mono,
                    mb: 1.5,
                  }}
                >
                  Fulfillment Location
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={selectedLocationId || ''}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: colors.surfaceLight,
                      borderRadius: '10px',
                      '& fieldset': { borderColor: colors.border },
                      '&:hover fieldset': { borderColor: colors.textMuted },
                      '&.Mui-focused fieldset': { borderColor: colors.accent, borderWidth: 2 },
                    },
                    '& .MuiSelect-select': { color: colors.textPrimary },
                  }}
                >
                  {locations.map((location: Location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name} {location.city && `(${location.city})`}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Divider sx={{ my: 3, borderColor: colors.border }} />

              {/* Product Search */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterSpacing.widest,
                    fontFamily: typography.fontFamily.mono,
                    mb: 1.5,
                  }}
                >
                  Add Products
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  inputRef={searchInputRef}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search products by name or SKU..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: productsLoading ? (
                      <InputAdornment position="end">
                        <CircularProgress size={18} sx={{ color: colors.accent }} />
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: colors.surfaceLight,
                      borderRadius: '10px',
                      '& fieldset': { borderColor: colors.border },
                      '&:hover fieldset': { borderColor: colors.textMuted },
                      '&.Mui-focused fieldset': { borderColor: colors.accent, borderWidth: 2 },
                    },
                    '& input': {
                      color: colors.textPrimary,
                      '&::placeholder': { color: colors.textMuted },
                    },
                  }}
                />
              </Box>

              {/* Product Grid */}
              <Box
                sx={{
                  maxHeight: 400,
                  overflowY: 'auto',
                  pr: 1,
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: colors.border,
                    borderRadius: 3,
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                    gap: 2,
                  }}
                >
                  {products.map((product: Product) => {
                    const cartItem = cart.find((item) => item.productId === product.id);
                    const quantity = cartItem?.quantity || 0;

                    return (
                      <Box
                        key={product.id}
                        sx={{
                          backgroundColor: colors.surfaceLight,
                          borderRadius: '12px',
                          overflow: 'hidden',
                          border: `1px solid ${quantity > 0 ? colors.accent : colors.border}`,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: colors.accent,
                            boxShadow: `0 4px 12px ${alpha(colors.accent, 0.15)}`,
                          },
                        }}
                      >
                        {/* Product Image */}
                        <Box
                          sx={{
                            height: 80,
                            backgroundColor: alpha(colors.textMuted, 0.05),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                          }}
                        >
                          {product.images && product.images.length > 0 ? (
                            <Box
                              component="img"
                              src={getImageUrl(product.images[0])}
                              alt={product.name}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <InventoryIcon sx={{ fontSize: 32, color: colors.textMuted, opacity: 0.4 }} />
                          )}
                          {quantity > 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 6,
                                right: 6,
                                width: 24,
                                height: 24,
                                borderRadius: '6px',
                                backgroundColor: colors.accent,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  color: '#FFF',
                                }}
                              >
                                {quantity}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Product Info */}
                        <Box sx={{ p: 1.5 }}>
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: 600,
                              color: colors.textPrimary,
                              mb: 0.5,
                              lineHeight: 1.3,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              minHeight: 31,
                            }}
                          >
                            {product.name}
                          </Typography>

                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                            <Typography
                              sx={{
                                fontSize: '12px',
                                fontWeight: 700,
                                color: colors.accent,
                              }}
                            >
                              {formatPrice(product.price)}
                            </Typography>

                            {quantity > 0 ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => updateQuantity(product.id, quantity - 1)}
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    backgroundColor: alpha(colors.error || '#EF4444', 0.1),
                                    color: colors.error || '#EF4444',
                                    '&:hover': {
                                      backgroundColor: alpha(colors.error || '#EF4444', 0.2),
                                    },
                                  }}
                                >
                                  <RemoveIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => updateQuantity(product.id, quantity + 1)}
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    backgroundColor: alpha(colors.success || '#22C55E', 0.1),
                                    color: colors.success || '#22C55E',
                                    '&:hover': {
                                      backgroundColor: alpha(colors.success || '#22C55E', 0.2),
                                    },
                                  }}
                                >
                                  <AddIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            ) : (
                              <IconButton
                                size="small"
                                onClick={() => addToCart(product)}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  backgroundColor: colors.accent,
                                  color: '#FFF',
                                  '&:hover': {
                                    backgroundColor: colors.accentLight,
                                    transform: 'scale(1.05)',
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                <AddIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}

                  {products.length === 0 && !productsLoading && (
                    <Box
                      sx={{
                        gridColumn: '1 / -1',
                        py: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <SearchIcon sx={{ fontSize: 40, color: colors.textMuted, opacity: 0.5 }} />
                      <Typography sx={{ color: colors.textMuted, fontSize: '13px' }}>
                        No products found
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* RIGHT COLUMN - Order Summary & Pricing */}
        <Box sx={{ ...animations.slideUp(0.2) }}>
          <Card
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: themeConfig.radius.sm,
              position: 'sticky',
              top: 20,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              {/* Order Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <ReceiptLongIcon sx={{ color: colors.accent, fontSize: 24 }} />
                <Typography
                  sx={{
                    fontSize: typography.fontSize.lg,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textPrimary,
                    fontFamily: typography.fontFamily.display,
                  }}
                >
                  Order Summary
                </Typography>
              </Box>

              {/* Cart Items */}
              {cart.length === 0 ? (
                <Box
                  sx={{
                    py: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                    color: colors.textMuted,
                    backgroundColor: alpha(colors.textMuted, 0.05),
                    borderRadius: '10px',
                    border: `1px dashed ${colors.border}`,
                  }}
                >
                  <StorefrontIcon sx={{ fontSize: 40, opacity: 0.4 }} />
                  <Typography sx={{ fontSize: '13px' }}>No items in order</Typography>
                </Box>
              ) : (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: typography.fontSize.xs,
                      fontWeight: typography.fontWeight.bold,
                      color: colors.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.widest,
                      fontFamily: typography.fontFamily.mono,
                      mb: 1.5,
                    }}
                  >
                    Items ({cart.reduce((sum, item) => sum + item.quantity, 0)})
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflowY: 'auto',
                      pr: 0.5,
                      '&::-webkit-scrollbar': { width: 4 },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: colors.border,
                        borderRadius: 2,
                      },
                    }}
                  >
                    {cart.map((item, index) => (
                      <Box
                        key={item.productId}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          py: 1.5,
                          borderBottom: index < cart.length - 1 ? `1px solid ${colors.border}` : 'none',
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: colors.textPrimary,
                              lineHeight: 1.3,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.productName}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '11px',
                              color: colors.textMuted,
                            }}
                          >
                            {item.quantity} x {formatPrice(item.retailPrice)}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: colors.textPrimary,
                          }}
                        >
                          {formatPrice(item.retailPrice * item.quantity)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => removeFromCart(item.productId)}
                          sx={{
                            width: 24,
                            height: 24,
                            color: colors.textMuted,
                            '&:hover': {
                              color: colors.error || '#EF4444',
                              backgroundColor: alpha(colors.error || '#EF4444', 0.1),
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                  {cart.length > 0 && (
                    <Button
                      size="small"
                      onClick={clearCart}
                      sx={{
                        mt: 1,
                        color: colors.textMuted,
                        fontSize: '11px',
                        '&:hover': { color: colors.error || '#EF4444' },
                      }}
                    >
                      Clear All
                    </Button>
                  )}
                </Box>
              )}

              {/* B2B Pricing Calculator */}
              {cart.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <B2BPricingCalculator
                    reseller={selectedReseller}
                    items={cart}
                    colors={colors}
                    compact={false}
                    onPricingCalculated={handlePricingCalculated}
                  />
                </Box>
              )}

              <Divider sx={{ my: 2, borderColor: colors.border }} />

              {/* Payment Options */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterSpacing.widest,
                    fontFamily: typography.fontFamily.mono,
                    mb: 1.5,
                  }}
                >
                  Payment Method
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {paymentMethods.map((method) => {
                    const Icon = method.icon;
                    const isSelected = paymentMethod === method.value;
                    return (
                      <Button
                        key={method.value}
                        onClick={() => setPaymentMethod(method.value)}
                        sx={{
                          flex: 1,
                          py: 1.5,
                          borderRadius: '10px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          backgroundColor: isSelected
                            ? alpha(colors.accent, 0.15)
                            : colors.surfaceLight,
                          border: `2px solid ${isSelected ? colors.accent : 'transparent'}`,
                          color: isSelected ? colors.accent : colors.textSecondary,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: isSelected
                              ? alpha(colors.accent, 0.2)
                              : alpha(colors.textMuted, 0.1),
                          },
                        }}
                      >
                        <Icon sx={{ fontSize: 20 }} />
                        <Typography
                          sx={{
                            fontSize: '10px',
                            fontWeight: 600,
                            textTransform: 'none',
                          }}
                        >
                          {method.label}
                        </Typography>
                      </Button>
                    );
                  })}
                </Box>
              </Box>

              {/* Payment Terms */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterSpacing.widest,
                    fontFamily: typography.fontFamily.mono,
                    mb: 1.5,
                  }}
                >
                  Payment Terms
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value as PaymentTerms)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: colors.surfaceLight,
                      borderRadius: '10px',
                      '& fieldset': { borderColor: colors.border },
                      '&:hover fieldset': { borderColor: colors.textMuted },
                      '&.Mui-focused fieldset': { borderColor: colors.accent, borderWidth: 2 },
                    },
                    '& .MuiSelect-select': { color: colors.textPrimary },
                  }}
                >
                  {paymentTermsOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Notes */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterSpacing.widest,
                    fontFamily: typography.fontFamily.mono,
                    mb: 1.5,
                  }}
                >
                  Notes (Optional)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  placeholder="Add order notes..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: colors.surfaceLight,
                      borderRadius: '10px',
                      '& fieldset': { borderColor: colors.border },
                      '&:hover fieldset': { borderColor: colors.textMuted },
                      '&.Mui-focused fieldset': { borderColor: colors.accent, borderWidth: 2 },
                    },
                    '& textarea': {
                      color: colors.textPrimary,
                      '&::placeholder': { color: colors.textMuted },
                    },
                  }}
                />
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => navigate('/app/sales-b2b')}
                  sx={{
                    py: 1.5,
                    borderRadius: themeConfig.radius.sm,
                    borderColor: colors.border,
                    color: colors.textSecondary,
                    fontWeight: typography.fontWeight.bold,
                    textTransform: 'uppercase',
                    fontSize: typography.fontSize.xs,
                    '&:hover': {
                      borderColor: colors.textMuted,
                      backgroundColor: alpha(colors.textMuted, 0.1),
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  disabled={
                    createOrderMutation.isPending ||
                    !selectedReseller ||
                    cart.length === 0 ||
                    !selectedLocationId
                  }
                  sx={{
                    py: 1.5,
                    borderRadius: themeConfig.radius.sm,
                    backgroundColor: palette.primary,
                    color: '#FFFFFF',
                    fontWeight: typography.fontWeight.bold,
                    textTransform: 'uppercase',
                    fontSize: typography.fontSize.sm,
                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    '&:hover': {
                      backgroundColor: alpha(palette.primary, 0.8),
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha(palette.primary, 0.3)}`,
                    },
                    '&:disabled': {
                      backgroundColor: colors.textMuted,
                      color: colors.surfaceLight,
                    },
                  }}
                >
                  {createOrderMutation.isPending ? 'Creating...' : 'Create B2B Order'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Rate Limit Upgrade Modal */}
      <UpgradeModal
        open={rateLimitModalOpen}
        onClose={() => setRateLimitModalOpen(false)}
        feature="Unlimited B2B Sales"
        currentTier={tier}
        customMessage={
          rateLimitInfo
            ? `You've reached your B2B sales rate limit (${rateLimitInfo.limit} per hour). Your limit will reset at ${new Date(rateLimitInfo.resetAt).toLocaleTimeString()}. Upgrade to remove all sales limits.`
            : undefined
        }
      />
    </Box>
  );
};

export default CreateB2BSale;
